/**
 * PortalCompleteProfile (/portal/complete-profile)
 *
 * Entry point post-pago para todas las variantes del funnel DualCTAVideoLanding.
 * Stripe y MercadoPago redirigen aquí después del pago.
 *
 * Flow:
 *   Pago completado → /portal/complete-profile → form (email, telegram, cumpleaños)
 *   → Clerk signUp (passwordless) → email verification → /portal (espera aprobación)
 *
 * Copy en primera persona, tono cálido. Bilingüe ES/EN inline.
 * La espera de aprobación se comunica de forma amigable y transparente.
 */

import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, useSignUp, useUser } from "@clerk/clerk-react";
import { isProfileComplete } from "@/lib/auth";
import {
  buildMembershipProfileMetadata,
  deriveMembershipAlias,
} from "../../shared/membershipClerkMetadata";
import {
  MembershipPortalCard,
  MembershipPortalIconBadge,
  MembershipPortalShell,
} from "@/components/membership/MembershipPortalShell";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@heroui/react";
import {
  AlertTriangle,
  CalendarDays,
  Cake,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Send,
  ShieldCheck,
  Sparkles,
  User,
} from "lucide-react";

import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useMembershipFunnelEnglish } from "@/hooks/useMembershipFunnelEnglish";
import { BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";

// ── i18n inline ──────────────────────────────────────────────────────────────
type ProfileCopy = {
  seoTitle: string;
  seoDescription: string;
  headingNew: string;
  headingVerify: string;
  subtitlePaid: string;
  subtitleNoPay: string;
  descVerify: (email: string) => string;
  paymentBadgeTitle: string;
  paymentBadgeBody: string;
  approvalTitle: string;
  approvalBody: string;
  alreadyHaveAccount: string;
  enterHere: string;
  verifyCodeLabel: string;
  verifyButton: string;
  firstName: string;
  firstNamePlaceholder: string;
  lastName: string;
  lastNamePlaceholder: string;
  optional: string;
  email: string;
  emailPlaceholder: string;
  emailInvalid: string;
  telegram: string;
  telegramPlaceholder: string;
  telegramExplain: string;
  telegramInvalid: string;
  birthday: string;
  birthdayAge: (n: number) => string;
  submitButton: string;
  validationError: string;
  signUpFailed: string;
  verifyCodeMismatch: string;
  verifyCodeInvalid: string;
};

const COPY_EN: ProfileCopy = {
  seoTitle: `Complete your profile — ${BRAND_PERSON_NAME}`,
  seoDescription: `Fill in your details to activate your ${BRAND_PERSON_NAME} membership.`,
  headingNew: "Almost there!",
  headingVerify: "Confirm your email",
  subtitlePaid: `Payment went through perfectly. Fill in your details in 30 seconds and I'll personally welcome you.`,
  subtitleNoPay: `Fill in your details in 30 seconds and I'll review your request personally.`,
  descVerify: (email) =>
    `Enter the code sent to ${email}. Almost done!`,
  paymentBadgeTitle: "Payment confirmed!",
  paymentBadgeBody:
    ` Fill in the form and I'll send your private link by email as soon as I review it.`,
  approvalTitle: "How long does approval take?",
  approvalBody: `I review every request personally. Usually within 24 hours you'll have your access. You can close this window — I'll send everything to your email when it's ready.`,
  alreadyHaveAccount: "Already have an account?",
  enterHere: "Sign in",
  verifyCodeLabel: "Your verification code",
  verifyButton: "Confirm email & get in",
  firstName: "Your first name",
  firstNamePlaceholder: "Jane",
  lastName: "Your last name",
  lastNamePlaceholder: "Doe",
  optional: "(optional)",
  email: "Your email",
  emailPlaceholder: "fan@email.com",
  emailInvalid: "Check the email — missing @ or domain.",
  telegram: "Your Telegram username",
  telegramPlaceholder: "yourhandle",
  telegramExplain:
    "I need it to send you your private access link to the group. Last step!",
  telegramInvalid:
    "Username must be 5–32 chars (letters, numbers, or underscore).",
  birthday: "Your date of birth",
  birthdayAge: (n) => `${n} years old — perfect!`,
  submitButton: "I'm in!",
  validationError:
    "Check the fields: email, Telegram username (@valid handle) and date of birth are required.",
  signUpFailed:
    "Something didn't go through. Check the details and try again.",
  verifyCodeMismatch: "Code doesn't match. Check your email and retry.",
  verifyCodeInvalid: "Invalid code. Try again.",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function normalizeTelegramAlias(raw: string): string {
  const cleaned = raw.trim().replace(/^@+/, "").replace(/\s+/g, "");
  return cleaned ? `@${cleaned}` : "";
}

function isValidTelegramAlias(alias: string): boolean {
  return /^[A-Za-z0-9_]{5,32}$/.test(alias.replace(/^@/, ""));
}

function calcAge(isoDate: string): number | null {
  if (!isoDate) return null;
  const birth = new Date(isoDate);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age;
}

function generatePasswordlessSecret(): string {
  const buf = new Uint8Array(24);
  crypto.getRandomValues(buf);
  const b64 = btoa(String.fromCharCode(...buf))
    .replace(/=+$/, "")
    .replace(/\+/g, "A")
    .replace(/\//g, "z");
  return `Q1!${b64}9z`;
}

const TODAY = new Date();
const MAX_BIRTH_DATE = new Date(
  TODAY.getFullYear() - 18,
  TODAY.getMonth(),
  TODAY.getDate(),
)
  .toISOString()
  .split("T")[0];
const MIN_BIRTH_DATE = new Date(TODAY.getFullYear() - 100, 0, 1)
  .toISOString()
  .split("T")[0];

// ── Component ─────────────────────────────────────────────────────────────────

export default function PortalCompleteProfile() {
  useMembershipFunnelEnglish();
  const { currentLanguage } = useLanguage();
  // Post-purchase surfaces render in ENGLISH always (decisión Andrés
  // 2026-07-02). currentLanguage se conserva solo como dato de registro.
  const t = COPY_EN;

  useSEO({
    title: t.seoTitle,
    description: t.seoDescription,
    keywords: "",
    ogImage: BRAND_OG_IMAGE_URL,
    canonical: `${BRAND_SITE_URL}/portal/complete-profile`,
    robots: "noindex, nofollow",
  });

  const navigate = useNavigate();
  const { search } = useLocation();
  const [params] = useSearchParams();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { isLoaded: signUpLoaded, signUp, setActive } = useSignUp();

  // Called after Clerk setActive() to durably persist profile data + mark registration done.
  // Fire-and-forget: failures are logged server-side but never block the user flow.
  const persistProfileAfterSignUp = (telegram: string, birthday: string) => {
    const provider =
      paymentIds.gateway === "stripe" || paymentIds.stripe_session_id
        ? "stripe"
        : paymentIds.mp_payment_id || paymentIds.mp_preapproval_id
          ? "mercadopago"
          : null;
    const externalId =
      paymentIds.stripe_session_id ||
      paymentIds.mp_preapproval_id ||
      paymentIds.mp_payment_id;
    if (!provider || !externalId) return;

    void fetch("/api/save-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        telegram_alias: telegram,
        birth_date: birthday,
        provider,
        external_id: externalId,
      }),
    }).catch(() => {});

    void fetch("/api/confirm-registration", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider, external_id: externalId }),
    }).catch(() => {});
  };

  const paymentIds = useMemo(
    () => ({
      stripe_session_id: params.get("session_id") || undefined,
      stripe_payment_intent_id: params.get("payment_intent") || undefined,
      stripe_subscription_id: params.get("subscription_id") || undefined,
      mp_payment_id:
        params.get("payment_id") || params.get("collection_id") || undefined,
      mp_preapproval_id: params.get("preapproval_id") || undefined,
      plan_tier: params.get("plan_tier")?.trim().toLowerCase() || undefined,
      gateway:
        params.get("provider") ||
        (params.get("session_id") ? "stripe" : undefined) ||
        (params.get("payment_id") ||
        params.get("collection_id") ||
        params.get("preapproval_id")
          ? "mercadopago"
          : undefined),
    }),
    [params],
  );

  const hasPaymentSignal = Boolean(
    paymentIds.stripe_session_id ||
      paymentIds.mp_payment_id ||
      paymentIds.mp_preapproval_id ||
      paymentIds.stripe_subscription_id,
  );

  const registrationNudgeAttribution = useMemo(() => {
    const utmCampaign = params.get("utm_campaign");
    const fromNudgeEmail =
      utmCampaign === "registration_nudge" ||
      params.get("utm_source") === "resend";
    return {
      from_nudge_email: fromNudgeEmail,
      utm_source: params.get("utm_source") || undefined,
      utm_medium: params.get("utm_medium") || undefined,
      utm_campaign: utmCampaign || undefined,
      utm_content: params.get("utm_content") || undefined,
    };
  }, [params]);

  const portalPath = useMemo(() => {
    const sp = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    sp.set("lang", "en");
    const q = sp.toString();
    return q ? `${MEMBERSHIP_ROUTE.PORTAL}?${q}` : `${MEMBERSHIP_ROUTE.PORTAL}?lang=en`;
  }, [search]);

  const track = (event: string, props: Record<string, unknown> = {}) => {
    if (typeof window === "undefined") return;
    const ph = (
      window as Window & {
        posthog?: { capture: (e: string, p?: object) => void };
      }
    ).posthog;
    ph?.capture(event, {
      ...props,
      has_payment_signal: hasPaymentSignal,
      gateway: paymentIds.gateway,
      plan_tier: paymentIds.plan_tier,
      language: currentLanguage,
      page: "portal_complete_profile",
      ...registrationNudgeAttribution,
    });
  };

  const trackNudgeFunnelStep = (funnelStep: string, extra: Record<string, unknown> = {}) => {
    if (!registrationNudgeAttribution.from_nudge_email) return;
    if (typeof window === "undefined") return;
    const ph = (
      window as Window & {
        posthog?: { capture: (e: string, p?: object) => void };
      }
    ).posthog;
    ph?.capture("conversion_funnel_step", {
      funnel: "membership_registration",
      funnel_step: funnelStep,
      email_channel: "resend",
      email_type: "registration_nudge",
      has_payment_signal: hasPaymentSignal,
      gateway: paymentIds.gateway,
      plan_tier: paymentIds.plan_tier,
      language: currentLanguage,
      page: "portal_complete_profile",
      ...registrationNudgeAttribution,
      ...extra,
    });
  };

  useEffect(() => {
    track("portal_complete_profile_view");
    trackNudgeFunnelStep("registration_nudge_landing_view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [telegramRaw, setTelegramRaw] = useState("");
  const [birthDate, setBirthDate] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyStep, setVerifyStep] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const telegramAlias = useMemo(
    () => normalizeTelegramAlias(telegramRaw),
    [telegramRaw],
  );
  const telegramValid = telegramAlias
    ? isValidTelegramAlias(telegramAlias)
    : false;
  const age = useMemo(() => calcAge(birthDate), [birthDate]);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const formValid = emailOk && telegramValid && birthDate.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUpLoaded || !signUp || submitting) return;
    if (!formValid) {
      track("portal_complete_profile_validation_failed", {
        has_email: emailOk,
        has_telegram: telegramValid,
        has_birthday: Boolean(birthDate),
      });
      setError(t.validationError);
      return;
    }
    setError(null);
    setSubmitting(true);
    track("portal_complete_profile_submitted", {
      has_first_name: Boolean(firstName.trim()),
      has_last_name: Boolean(lastName.trim()),
    });
    trackNudgeFunnelStep("registration_nudge_profile_submitted", {
      has_first_name: Boolean(firstName.trim()),
      has_last_name: Boolean(lastName.trim()),
    });

    try {
      const derivedAlias = deriveMembershipAlias({
        firstName,
        lastName,
        telegram: telegramAlias,
        email,
      });
      const unsafeMetadata = buildMembershipProfileMetadata(
        {
          alias: derivedAlias,
          telegram: telegramAlias,
          birthDate,
        },
        {
          ...(paymentIds.stripe_session_id && {
            stripe_session_id: paymentIds.stripe_session_id,
          }),
          ...(paymentIds.stripe_payment_intent_id && {
            stripe_payment_intent_id: paymentIds.stripe_payment_intent_id,
          }),
          ...(paymentIds.stripe_subscription_id && {
            stripe_subscription_id: paymentIds.stripe_subscription_id,
          }),
          ...(paymentIds.mp_payment_id && {
            mp_payment_id: paymentIds.mp_payment_id,
          }),
          ...(paymentIds.mp_preapproval_id && {
            mp_preapproval_id: paymentIds.mp_preapproval_id,
          }),
          ...(paymentIds.plan_tier && { plan_tier: paymentIds.plan_tier }),
          ...(paymentIds.gateway && { gateway: paymentIds.gateway }),
          membership_status: "pending_approval",
          registered_via: "portal_complete_profile_v1",
        },
      );

      const randomPassword = generatePasswordlessSecret();
      await signUp.create({
        emailAddress: email,
        password: randomPassword,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        unsafeMetadata,
      });

      const needsEmailVerify =
        signUp.unverifiedFields?.includes("email_address");
      if (needsEmailVerify) {
        await signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });
        track("portal_complete_profile_verify_step_shown");
        setVerifyStep(true);
      } else {
        track("portal_complete_profile_completed", { skipped_verify: true });
        trackNudgeFunnelStep("registration_nudge_profile_completed", {
          skipped_verify: true,
        });
        if (signUp.createdSessionId) {
          await setActive({ session: signUp.createdSessionId });
        }
        persistProfileAfterSignUp(telegramAlias, birthDate);
        navigate(portalPath, { replace: true });
      }
    } catch (err) {
      const msg =
        err && typeof err === "object" && "errors" in err
          ? (
              err as {
                errors?: Array<{ message?: string; longMessage?: string }>;
              }
            ).errors?.[0]?.longMessage ??
            (
              err as { errors?: Array<{ message?: string }> }
            ).errors?.[0]?.message ??
            t.signUpFailed
          : t.signUpFailed;
      track("portal_complete_profile_signup_error", {
        message: String(msg).slice(0, 200),
      });
      setError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUpLoaded || !signUp || verifying) return;
    setError(null);
    setVerifying(true);
    track("portal_complete_profile_verify_submitted");
    try {
      const result = await signUp.attemptEmailAddressVerification({
        code: verifyCode,
      });
      if (result.status === "complete" && result.createdSessionId) {
        track("portal_complete_profile_completed", { skipped_verify: false });
        trackNudgeFunnelStep("registration_nudge_profile_completed", {
          skipped_verify: false,
        });
        await setActive({ session: result.createdSessionId });
        persistProfileAfterSignUp(telegramAlias, birthDate);
        navigate(portalPath, { replace: true });
      } else {
        track("portal_complete_profile_verify_mismatch");
        setError(t.verifyCodeMismatch);
      }
    } catch (err) {
      const msg =
        err && typeof err === "object" && "errors" in err
          ? (err as { errors?: Array<{ message?: string }> }).errors?.[0]
              ?.message ?? t.verifyCodeInvalid
          : t.verifyCodeInvalid;
      track("portal_complete_profile_verify_error", {
        message: String(msg).slice(0, 200),
      });
      setError(String(msg));
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const prefillEmail = params.get("email")?.trim();
    if (prefillEmail && !email) setEmail(prefillEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Post-Stripe: prefill email desde Checkout Session (mismo email del cobro).
  useEffect(() => {
    const sessionId = paymentIds.stripe_session_id?.trim();
    if (!sessionId?.startsWith("cs_") || email) return;
    let cancelled = false;
    void fetch(`/api/stripe-session?session_id=${encodeURIComponent(sessionId)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { customer_email?: string } | null) => {
        if (cancelled || !data?.customer_email) return;
        setEmail(data.customer_email.trim());
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIds.stripe_session_id]);

  if (authLoaded && isSignedIn) {
    if (isProfileComplete(user)) {
      return <Navigate to={portalPath} replace />;
    }
    const sp = new URLSearchParams(params);
    sp.set("lang", "en");
    return (
      <Navigate
        to={`${MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO}?${sp.toString()}`}
        replace
      />
    );
  }

  if (!authLoaded || !signUpLoaded) {
    return (
      <MembershipPortalShell>
        <MembershipPortalCard className="animate-pulse p-6 sm:p-8">
          <div className="mx-auto mb-6 h-14 w-14 rounded-2xl bg-white/10" />
          <div className="mx-auto mb-3 h-6 w-52 rounded-lg bg-white/10" />
          <div className="mx-auto mb-8 h-4 w-64 max-w-full rounded bg-white/5" />
          <div className="space-y-3">
            <div className="h-11 rounded-xl bg-white/[0.06]" />
            <div className="h-11 rounded-xl bg-white/[0.06]" />
            <div className="h-11 rounded-xl bg-white/[0.06]" />
            <div className="h-12 rounded-xl bg-amber-500/15" />
          </div>
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Preparing your profile…
          </div>
        </MembershipPortalCard>
      </MembershipPortalShell>
    );
  }

  const profileProgress = (
    <ol
      className="mb-1 grid grid-cols-3 gap-1.5 text-center text-[10px] font-medium uppercase tracking-wide sm:text-[11px]"
      aria-label="Profile progress"
    >
      {[
        {
          key: "pay",
          label: hasPaymentSignal ? "Paid" : "Start",
          done: hasPaymentSignal,
          active: false,
        },
        {
          key: "profile",
          label: verifyStep ? "Verify" : "Profile",
          done: false,
          active: true,
        },
        {
          key: "portal",
          label: "Portal",
          done: false,
          active: false,
        },
      ].map((step, i) => (
        <li
          key={step.key}
          className={`rounded-xl border px-1.5 py-2 ${
            step.done
              ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-200"
              : step.active
                ? "border-amber-500/40 bg-amber-500/10 text-amber-100"
                : "border-white/10 bg-white/[0.03] text-zinc-500"
          }`}
        >
          <span className="mb-0.5 block tabular-nums opacity-70">{i + 1}</span>
          {step.label}
        </li>
      ))}
    </ol>
  );

  return (
    <MembershipPortalShell
      topSlot={
        hasPaymentSignal && !verifyStep ? (
          <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3.5 backdrop-blur-sm">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" aria-hidden />
            <p className="text-sm leading-relaxed text-emerald-100">
              <strong className="text-emerald-200">{t.paymentBadgeTitle}</strong>
              {t.paymentBadgeBody}
            </p>
          </div>
        ) : null
      }
    >
      <MembershipPortalCard>
          <CardHeader className="space-y-3 pb-4 pt-7 text-center">
            <MembershipPortalIconBadge>
              {verifyStep ? (
                <ShieldCheck className="h-7 w-7" />
              ) : (
                <Sparkles className="h-7 w-7" />
              )}
            </MembershipPortalIconBadge>
            <CardTitle className="font-ios-display text-2xl font-semibold tracking-tight text-zinc-50">
              {verifyStep ? t.headingVerify : t.headingNew}
            </CardTitle>
            <CardDescription className="mx-auto max-w-sm text-sm leading-relaxed text-zinc-400">
              {verifyStep
                ? t.descVerify(email)
                : hasPaymentSignal
                  ? t.subtitlePaid
                  : t.subtitleNoPay}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4 px-5 pb-6 pt-1 sm:px-6">
            {profileProgress}
            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs leading-relaxed text-rose-100"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" aria-hidden />
                <p>{error}</p>
              </div>
            )}

            {verifyStep ? (
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="verify-code"
                    className="text-zinc-300"
                  >
                    {t.verifyCodeLabel}
                  </Label>
                  <Input
                    id="verify-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={verifyCode}
                    onChange={(e) =>
                      setVerifyCode(
                        e.target.value.replace(/\D/g, "").slice(0, 8),
                      )
                    }
                    className="bg-black/30 border-white/10 text-zinc-100 text-center text-lg tracking-[0.4em] font-mono h-12"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  color="warning"
                  className="ios-press h-12 w-full bg-amber-500 font-semibold text-amber-950"
                  isLoading={verifying}
                  isDisabled={verifyCode.length < 4}
                >
                  {t.verifyButton}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nombre + Apellido (opcionales) */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="first-name"
                      className="flex items-center gap-1.5 text-zinc-300 text-xs"
                    >
                      <User className="h-3 w-3" /> {t.firstName}
                      <span className="text-zinc-600">{t.optional}</span>
                    </Label>
                    <Input
                      id="first-name"
                      type="text"
                      autoComplete="given-name"
                      placeholder={t.firstNamePlaceholder}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="bg-black/30 border-white/10 text-zinc-100 h-11"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="last-name"
                      className="text-zinc-300 text-xs"
                    >
                      {t.lastName}
                      <span className="text-zinc-600 ml-1">{t.optional}</span>
                    </Label>
                    <Input
                      id="last-name"
                      type="text"
                      autoComplete="family-name"
                      placeholder={t.lastNamePlaceholder}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="bg-black/30 border-white/10 text-zinc-100 h-11"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <Label
                    htmlFor="email"
                    className="flex items-center gap-1.5 text-zinc-300 text-xs"
                  >
                    <Mail className="h-3 w-3" /> {t.email}
                    <span className="text-amber-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t.emailPlaceholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-black/30 border-white/10 text-zinc-100 h-11"
                    required
                  />
                  {!emailOk && email.length > 0 && (
                    <p className="text-[11px] text-rose-400">{t.emailInvalid}</p>
                  )}
                </div>

                {/* Telegram */}
                <div className="space-y-1.5">
                  <Label
                    htmlFor="telegram"
                    className="flex items-center gap-1.5 text-zinc-300 text-xs"
                  >
                    <Send className="h-3 w-3" /> {t.telegram}
                    <span className="text-amber-400">*</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-zinc-500 text-sm pointer-events-none">
                      @
                    </span>
                    <Input
                      id="telegram"
                      type="text"
                      autoComplete="off"
                      placeholder={t.telegramPlaceholder}
                      value={telegramRaw.replace(/^@/, "")}
                      onChange={(e) => setTelegramRaw(e.target.value)}
                      className="bg-black/30 border-white/10 text-zinc-100 h-11 pl-7"
                      required
                    />
                  </div>
                  <p className="text-[11px] text-zinc-500 leading-relaxed">
                    {t.telegramExplain}
                  </p>
                  {telegramRaw && !telegramValid && (
                    <p className="text-[11px] text-rose-400">
                      {t.telegramInvalid}
                    </p>
                  )}
                </div>

                {/* Fecha de nacimiento */}
                <div className="space-y-1.5">
                  <Label
                    htmlFor="birth-date"
                    className="flex items-center gap-1.5 text-zinc-300 text-xs"
                  >
                    <Cake className="h-3 w-3" /> {t.birthday}
                    <span className="text-amber-400">*</span>
                  </Label>
                  <div className="relative">
                    <CalendarDays className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
                    <Input
                      id="birth-date"
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      min={MIN_BIRTH_DATE}
                      max={MAX_BIRTH_DATE}
                      className="bg-black/30 border-white/10 text-zinc-100 h-11 pr-10"
                      required
                    />
                  </div>
                  {age != null && (
                    <p className="text-[11px] text-amber-300/80">
                      {t.birthdayAge(age)}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  color="warning"
                  className="w-full h-12 bg-amber-500 text-amber-950 font-semibold mt-2"
                  isLoading={submitting}
                  isDisabled={!formValid || submitting}
                >
                  {t.submitButton}
                </Button>
              </form>
            )}

            <p className="pt-1 text-center text-[11px] text-zinc-600">
              {t.alreadyHaveAccount}{" "}
              <a
                href={`${MEMBERSHIP_ROUTE.ENTRAR}${search}`}
                className="text-amber-400 underline hover:text-amber-300"
              >
                {t.enterHere}
              </a>
            </p>
          </CardContent>
        </MembershipPortalCard>

        {/* ── Approval wait card ────────────────────────────────────────────── */}
        <div className="flex items-start gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] px-4 py-4 backdrop-blur-sm">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-400/70" />
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-zinc-300">{t.approvalTitle}</p>
            <p className="text-[11px] leading-relaxed text-zinc-500">
              {t.approvalBody}
            </p>
          </div>
        </div>

        {/* ── Powered by Stripe (only for Stripe payments) ─────────────────── */}
        {(paymentIds.gateway === "stripe" || !!paymentIds.stripe_session_id) && (
          <div className="flex justify-center pt-1">
            <a
              href="https://stripe.com"
              target="_blank"
              rel="noopener noreferrer"
              className="opacity-40 transition-opacity hover:opacity-60"
              aria-label="Powered by Stripe"
            >
              <img
                src="https://res.cloudinary.com/nm-creators/image/upload/v1781711564/Powered_by_Stripe_-_white_csam6q.svg"
                alt="Powered by Stripe"
                className="h-6 w-auto"
                loading="lazy"
              />
            </a>
          </div>
        )}
    </MembershipPortalShell>
  );
}
